

import type { HereMarker } from 'components/Common/HereMaps/HereRouteMap';
import { fetchHereRoute } from 'components/Common/HereMaps/hereMaps';
import { useEffect, useMemo, useRef, useState } from 'react';

interface Pickup {
  latitude: number
  longitude: number
}

interface MapBounds {
  north: number
  south: number
  east: number
  west: number
}

interface UseSupplierAnimationOptions {
  pickup: Pickup | undefined | null
  isActive: boolean

  bounds?: MapBounds | null

  originPoolSize?: number

  minDistanceKm?: number

  maxDistanceKm?: number

  travelMs?: number

  spawnIntervalMs?: number
}

interface Origin {
  key: string
  position: { latitude: number; longitude: number }
}

interface Trip {
  id: string
  origin: Origin
  startMs: number
}

interface RouteSamples {
  points: { latitude: number; longitude: number }[]
  cumulative: number[]
  total: number
}

const KM_PER_DEG_LAT = 111

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t
}

function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2
}

function offsetLatLng(
  origin: Pickup,
  bearingRad: number,
  distanceKm: number,
): { latitude: number; longitude: number } {
  const latOffsetDeg = (distanceKm / KM_PER_DEG_LAT) * Math.cos(bearingRad)
  const kmPerDegLng =
    KM_PER_DEG_LAT * Math.cos((origin.latitude * Math.PI) / 180)
  const lngOffsetDeg =
    (distanceKm / Math.max(kmPerDegLng, 1)) * Math.sin(bearingRad)
  return {
    latitude: origin.latitude + latOffsetDeg,
    longitude: origin.longitude + lngOffsetDeg,
  }
}

function pickOriginInBounds(
  pickup: Pickup,
  bounds: MapBounds,
  minDistanceKm: number,
  maxDistanceKm: number,
  marginRatio = 0.15,
): { latitude: number; longitude: number } {
  const latRange = bounds.north - bounds.south
  const lngRange = bounds.east - bounds.west
  const south = bounds.south + latRange * marginRatio
  const north = bounds.north - latRange * marginRatio
  const west = bounds.west + lngRange * marginRatio
  const east = bounds.east - lngRange * marginRatio

  const minDeg = minDistanceKm / KM_PER_DEG_LAT
  const minDegSq = minDeg * minDeg

  for (let attempt = 0; attempt < 25; attempt += 1) {
    const lat = lerp(south, north, Math.random())
    const lng = lerp(west, east, Math.random())
    const dLat = lat - pickup.latitude
    const dLng = lng - pickup.longitude
    if (dLat * dLat + dLng * dLng >= minDegSq) {
      return { latitude: lat, longitude: lng }
    }
  }

  const halfShortest = Math.min(north - south, east - west) / 2
  const cappedDistanceKm = Math.min(maxDistanceKm, halfShortest * KM_PER_DEG_LAT)
  return offsetLatLng(pickup, Math.random() * Math.PI * 2, cappedDistanceKm)
}

function buildRouteSamples(
  points: { latitude: number; longitude: number }[],
): RouteSamples {
  const cumulative: number[] = [0]
  for (let i = 1; i < points.length; i += 1) {
    const dLat = points[i].latitude - points[i - 1].latitude

    const meanLat = (points[i].latitude + points[i - 1].latitude) / 2
    const dLng =
      (points[i].longitude - points[i - 1].longitude) *
      Math.cos((meanLat * Math.PI) / 180)
    cumulative.push(cumulative[i - 1] + Math.sqrt(dLat * dLat + dLng * dLng))
  }
  return {
    points,
    cumulative,
    total: cumulative[cumulative.length - 1] || 0,
  }
}

function positionAlongRoute(
  samples: RouteSamples,
  t: number,
): { latitude: number; longitude: number } {
  if (samples.points.length === 0) return { latitude: 0, longitude: 0 }
  if (samples.total === 0 || samples.points.length === 1) {
    return samples.points[0]
  }
  const target = Math.max(0, Math.min(1, t)) * samples.total
  let i = 1
  while (i < samples.cumulative.length && samples.cumulative[i] < target) {
    i += 1
  }
  if (i >= samples.cumulative.length) {
    return samples.points[samples.points.length - 1]
  }
  const segStart = samples.cumulative[i - 1]
  const segEnd = samples.cumulative[i]
  const segT = segEnd === segStart ? 0 : (target - segStart) / (segEnd - segStart)
  const a = samples.points[i - 1]
  const b = samples.points[i]
  return {
    latitude: lerp(a.latitude, b.latitude, segT),
    longitude: lerp(a.longitude, b.longitude, segT),
  }
}

export default function useSupplierAnimation({
  pickup,
  isActive,
  bounds,
  originPoolSize = 10,
  minDistanceKm = 0.6,
  maxDistanceKm = 2.5,
  travelMs = 2400,
  spawnIntervalMs = 800,
}: UseSupplierAnimationOptions): HereMarker[] {
  const pickupKey =
    pickup?.latitude != null && pickup?.longitude != null
      ? `${pickup.latitude.toFixed(5)},${pickup.longitude.toFixed(5)}`
      : null

  const [sessionId, setSessionId] = useState(0)
  const wasActiveRef = useRef(false)
  useEffect(() => {
    if (isActive && !wasActiveRef.current) {
      setSessionId((n) => n + 1)
    }
    wasActiveRef.current = isActive
  }, [isActive])

  const [origins, setOrigins] = useState<Origin[]>([])
  const generatedForSessionRef = useRef(0)

  useEffect(() => {
    if (!isActive) {

      if (origins.length > 0) setOrigins([])
      generatedForSessionRef.current = 0
      return
    }
    if (
      !pickup ||
      pickupKey == null ||
      sessionId === 0 ||
      !bounds ||
      generatedForSessionRef.current === sessionId
    ) {
      return
    }
    const out: Origin[] = []
    for (let i = 0; i < originPoolSize; i += 1) {
      out.push({
        key: `o-${sessionId}-${i}`,
        position: pickOriginInBounds(
          pickup,
          bounds,
          minDistanceKm,
          maxDistanceKm,
        ),
      })
    }
    generatedForSessionRef.current = sessionId
    setOrigins(out)
  }, [
    isActive,
    pickup,
    pickupKey,
    bounds,
    sessionId,
    originPoolSize,
    minDistanceKm,
    maxDistanceKm,
    origins.length,
  ])

  const [routes, setRoutes] = useState<Record<string, RouteSamples>>({})

  useEffect(() => {
    if (!isActive || !pickup || origins.length === 0 || !pickupKey) {
      return undefined
    }
    const apiKey = process.env.REACT_APP_HERE_MAPS_API_KEY
    if (!apiKey) return undefined
    const aborters: AbortController[] = []
    origins.forEach((origin) => {
      const cacheKey = origin.key
      if (routes[cacheKey]) return
      const controller = new AbortController()
      aborters.push(controller)
      fetchHereRoute({
        apiKey,
        origin: { lat: origin.position.latitude, lng: origin.position.longitude },
        destination: { lat: pickup.latitude, lng: pickup.longitude },
        signal: controller.signal,
      })
        .then((result) => {
          if (controller.signal.aborted) return
          const flat = result.polylines.flatMap((section) =>
            section.map((p) => ({ latitude: p.lat, longitude: p.lng })),
          )
          if (flat.length < 2) return
          setRoutes((prev) =>
            prev[cacheKey] ? prev : { ...prev, [cacheKey]: buildRouteSamples(flat) },
          )
        })
        .catch(() => {
          // Silent - animation falls back to straight line.
        })
    })
    return () => {
      aborters.forEach((c) => c.abort())
    }
    // `routes` intentionally excluded - successful fetches set state and
    // would re-trigger this effect, causing infinite refetches.
  }, [isActive, pickupKey, origins, pickup])

  const tripsRef = useRef<Trip[]>([])
  const startedAtRef = useRef<number | null>(null)
  const lastSpawnRef = useRef(0)
  const tripCounterRef = useRef(0)
  const rafRef = useRef<number | null>(null)
  const [tick, setTick] = useState(0)

  useEffect(() => {
    if (!isActive || !pickup || origins.length === 0) {
      tripsRef.current = []
      startedAtRef.current = null
      lastSpawnRef.current = 0
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = null
      }
      return undefined
    }
    startedAtRef.current = performance.now()
    lastSpawnRef.current = -spawnIntervalMs
    tripsRef.current = []

    const loop = (now: number) => {
      const elapsed = now - (startedAtRef.current ?? now)

      if (elapsed - lastSpawnRef.current >= spawnIntervalMs) {
        const origin = origins[Math.floor(Math.random() * origins.length)]
        tripCounterRef.current += 1
        tripsRef.current.push({
          id: `trip-${tripCounterRef.current}`,
          origin,
          startMs: elapsed,
        })
        lastSpawnRef.current = elapsed
      }

      tripsRef.current = tripsRef.current.filter(
        (trip) => elapsed - trip.startMs < travelMs,
      )

      setTick(elapsed)
      rafRef.current = requestAnimationFrame(loop)
    }
    rafRef.current = requestAnimationFrame(loop)
    return () => {
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = null
      }
    }
  }, [isActive, pickup, origins, travelMs, spawnIntervalMs])

  return useMemo<HereMarker[]>(() => {
    if (!isActive || !pickup) return []
    const elapsed = tick
    return tripsRef.current.map((trip) => {
      const tripElapsed = elapsed - trip.startMs
      const t = easeInOutCubic(Math.max(0, Math.min(1, tripElapsed / travelMs)))
      const samples = routes[trip.origin.key]
      const position = samples
        ? positionAlongRoute(samples, t)
        : {
            latitude: lerp(trip.origin.position.latitude, pickup.latitude, t),
            longitude: lerp(trip.origin.position.longitude, pickup.longitude, t),
          }
      return {
        key: trip.id,
        position,
        kind: 'dot',
      }
    })
  }, [tick, pickup, isActive, travelMs, routes])
}
